import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AuthContext } from '../../../context/AuthContext';
import { NavigationContext } from '../../../context/NavigationContext';
import { toast } from 'react-toastify';
import * as XLSX from '@e965/xlsx';

export interface ExcelRow {
    [key: string]: string | number | null;
}

export interface Subscription {
    id: number;
    description: string | null;
    annualPrice: number;
    taxRate: number;
    type: string;
    ownerRoundTableId: number | null;
    ownerRoundTable?: { id: number; name: string; number: number } | null;
    mostRecentRechnungId: number | null;
    createdAt: string;
}

export interface NewSubscriptionData {
    description: string;
    annualPrice: number;
    taxRate: number;
    type: string;
    selected: boolean;
}

interface SubscriptionImportContextValue {
    // Wizard state
    currentStep: number;
    setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
    loading: boolean;
    importing: boolean;
    setImporting: React.Dispatch<React.SetStateAction<boolean>>;

    // Step 1
    excelData: ExcelRow[];
    columns: string[];
    descriptionColumn: string;
    setDescriptionColumn: React.Dispatch<React.SetStateAction<string>>;
    handleFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;

    // Step 2
    existingTypes: string[];
    typeSelection: 'existing' | 'new';
    setTypeSelection: React.Dispatch<React.SetStateAction<'existing' | 'new'>>;
    selectedType: string;
    setSelectedType: React.Dispatch<React.SetStateAction<string>>;
    newTypeName: string;
    setNewTypeName: React.Dispatch<React.SetStateAction<string>>;

    // Step 3
    newSubscriptions: NewSubscriptionData[];
    existingCount: number;
    handleToggleSubscription: (index: number) => void;
    handleSelectAll: () => void;
    handleDeselectAll: () => void;
    handleImport: (onImported?: (c: number) => void, onClose?: () => void) => Promise<void>;

    // Derived flags
    canProceedStep1: boolean;
    canProceedStep2: boolean;
}

const SubscriptionImportContext = createContext<SubscriptionImportContextValue | null>(null);

export const useSubscriptionImport = () => {
    const ctx = useContext(SubscriptionImportContext);
    if (!ctx) throw new Error('useSubscriptionImport must be used within SubscriptionImportProvider');
    return ctx;
};

export const SubscriptionImportProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const auth = useContext(AuthContext);
    const nav = useContext(NavigationContext);
    if (!auth) throw new Error('AuthContext missing');
    if (!nav) throw new Error('NavigationContext missing');

    const { token } = auth;
    const { navigateTo } = nav;

    // Wizard state
    const [currentStep, setCurrentStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);

    // Step 1 state
    const [excelData, setExcelData] = useState<ExcelRow[]>([]);
    const [columns, setColumns] = useState<string[]>([]);
    const [descriptionColumn, setDescriptionColumn] = useState<string>('');

    // Step 2 state
    const [existingTypes, setExistingTypes] = useState<string[]>([]);
    const [typeSelection, setTypeSelection] = useState<'existing' | 'new'>('existing');
    const [selectedType, setSelectedType] = useState<string>('');
    const [newTypeName, setNewTypeName] = useState<string>('');

    // Step 3 state
    const [newSubscriptions, setNewSubscriptions] = useState<NewSubscriptionData[]>([]);
    const [existingCount, setExistingCount] = useState(0);

    // Load existing types when reaching step 2
    useEffect(() => {
        if (currentStep === 2) {
            (async () => {
                try {
                    setLoading(true);
                    const res = await fetch('/api/subscriptions/types', { headers: { Authorization: `Bearer ${token}` } });
                    if (!res.ok) throw new Error('Fehler beim Laden der Typen');
                    const types = await res.json();
                    setExistingTypes(types);
                    if (types.length > 0 && !selectedType) setSelectedType(types[0]);
                } catch (e) {
                    console.error(e);
                    toast.error(e instanceof Error ? e.message : 'Fehler beim Laden der Typen');
                } finally {
                    setLoading(false);
                }
            })();
        }
    }, [currentStep, token, selectedType]);

    // Load existing subscriptions and match when reaching step 3
    useEffect(() => {
        if (currentStep === 3) {
            (async () => {
                try {
                    setLoading(true);
                    const res = await fetch('/api/subscriptions', { headers: { Authorization: `Bearer ${token}` } });
                    if (!res.ok) throw new Error('Fehler beim Laden der Abonnements');
                    const subs: Subscription[] = await res.json();
                    matchData(subs);
                } catch (e) {
                    console.error(e);
                    toast.error(e instanceof Error ? e.message : 'Fehler beim Laden der Abonnements');
                } finally {
                    setLoading(false);
                }
            })();
        }
    }, [currentStep, token, typeSelection, newTypeName, selectedType, descriptionColumn, excelData]);

    const matchData = useCallback((existingSubs: Subscription[]) => {
        const finalType = typeSelection === 'new' ? newTypeName : selectedType;
        const existingDescriptions = new Set(existingSubs.map(s => (s.description || '').toLowerCase().trim()));

        const newSubs: NewSubscriptionData[] = [];
        let existingMatch = 0;
        excelData.forEach(row => {
            const description = String(row[descriptionColumn] || '').trim();
            if (!description) return;
            const descLower = description.toLowerCase();
            if (existingDescriptions.has(descLower)) {
                existingMatch++;
            } else {
                newSubs.push({ description, annualPrice: 0, taxRate: 19, type: finalType, selected: true });
            }
        });
        setNewSubscriptions(newSubs);
        setExistingCount(existingMatch);
    }, [excelData, descriptionColumn, typeSelection, newTypeName, selectedType]);

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = e => {
            try {
                const data = new Uint8Array(e.target?.result as ArrayBuffer);
                const workbook = XLSX.read(data, { type: 'array' });
                const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json<ExcelRow>(firstSheet, { defval: null });
                if (jsonData.length === 0) {
                    toast.error('Die Datei enthält keine Daten');
                    return;
                }
                const cols = Object.keys(jsonData[0]);
                setColumns(cols);
                setExcelData(jsonData);
                const descCol = cols.find(c => c.toLowerCase().includes('beschreibung') || c.toLowerCase().includes('description') || c.toLowerCase().includes('name') || c.toLowerCase().includes('domain')) || cols[0];
                setDescriptionColumn(descCol);
                toast.success(`${jsonData.length} Zeilen geladen`);
            } catch (error) {
                console.error('Error reading file:', error);
                toast.error('Fehler beim Lesen der Datei');
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const handleToggleSubscription = (index: number) => {
        setNewSubscriptions(prev => prev.map((sub, i) => (i === index ? { ...sub, selected: !sub.selected } : sub)));
    };
    const handleSelectAll = () => setNewSubscriptions(prev => prev.map(sub => ({ ...sub, selected: true })));
    const handleDeselectAll = () => setNewSubscriptions(prev => prev.map(sub => ({ ...sub, selected: false })));

    const handleImport = async (onImported?: (createdCount: number) => void, onClose?: () => void) => {
        const selectedSubs = newSubscriptions.filter(s => s.selected);
        if (selectedSubs.length === 0) {
            toast.warn('Keine Abonnements zum Importieren ausgewählt');
            return;
        }
        try {
            setImporting(true);
            const res = await fetch('/api/subscriptions/bulk', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ subscriptions: selectedSubs.map(s => ({ description: s.description, annualPrice: s.annualPrice, taxRate: s.taxRate, type: s.type })) })
            });
            if (!res.ok) {
                const error = await res.json();
                throw new Error(error.message || 'Fehler beim Importieren');
            }
            const created = await res.json();
            toast.success(`${created.length} Abonnements erfolgreich importiert!`);
            if (onImported) onImported(created.length);
            if (onClose) onClose(); else navigateTo('/subscriptions');
        } catch (e) {
            console.error(e);
            toast.error(e instanceof Error ? e.message : 'Fehler beim Importieren');
        } finally {
            setImporting(false);
        }
    };

    const canProceedStep1 = excelData.length > 0 && !!descriptionColumn;
    const canProceedStep2 = (typeSelection === 'existing' && !!selectedType) || (typeSelection === 'new' && newTypeName.trim().length > 0);

    const value: SubscriptionImportContextValue = {
        currentStep,
        setCurrentStep,
        loading,
        importing,
        setImporting,
        excelData,
        columns,
        descriptionColumn,
        setDescriptionColumn,
        handleFileUpload,
        existingTypes,
        typeSelection,
        setTypeSelection,
        selectedType,
        setSelectedType,
        newTypeName,
        setNewTypeName,
        newSubscriptions,
        existingCount,
        handleToggleSubscription,
        handleSelectAll,
        handleDeselectAll,
        handleImport,
        canProceedStep1,
        canProceedStep2
    };

    return <SubscriptionImportContext.Provider value={value}>{children}</SubscriptionImportContext.Provider>;
};
